import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { campaignsEndpoints as endpoints } from '@/api/endpoints';
import { ApiError } from '@/api/fetcher';
import { CampaignSubscriberWithJourney } from '@/shared/types/campaigns';

import { API } from './useCampaignSubscribers';

export const useReevaluateCampaignSubscriber = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: reevaluateSubscriber, status: reevaluateSubscriberStatus } = useMutation({
    mutationFn: ({ campaignId, subscriberId }: { campaignId: string; subscriberId: string }) =>
      API.reevaluateCampaignSubscriber(campaignId, subscriberId),
    retry: false,
    onSuccess: (_data, variables) => {
      toast.success(t('campaigns.subscriber_journey.next_action.reevaluate_success'));
      queryClient.invalidateQueries({
        queryKey: [endpoints.campaignSubscribers(variables.campaignId)],
      });
      queryClient.setQueryData<CampaignSubscriberWithJourney>(
        [endpoints.campaignSubscriber(variables.campaignId, variables.subscriberId)],
        (prev) => (prev ? { ...prev, next_action: { state: 'deciding' } } : prev)
      );
    },
    onError: (error: ApiError) => {
      toast.error(
        error.statusCode === 409
          ? t('campaigns.subscriber_journey.next_action.reevaluate_not_active')
          : error.message
      );
    },
  });

  return { reevaluateSubscriber, reevaluateSubscriberStatus };
};
